import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Card } from '@/components/ui/Card';
import { useAuth } from '@/core/auth/AuthContext';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useCreateEstimate } from '../shared/hooks/useCreateEstimate';

function makeEstimateCode() {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  return `EST-${stamp}-${String(now.getTime()).slice(-4)}`;
}

export function EstimateNewPage() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const companyId = profile?.company_id || '';
  const { projects, isLoading } = useProjects(companyId, { pageSize: 50, type: 'landscape' });
  const { createEstimate, isCreating } = useCreateEstimate();
  const defaultCode = useMemo(() => makeEstimateCode(), []);
  const [form, setForm] = useState({
    estimate_code: defaultCode,
    estimate_name: '',
    estimate_type: 'project',
    project_id: '',
    customer_name: '',
    description: '',
  });

  const selectedProject = projects.find((project) => project.id === form.project_id);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!companyId) return;
    const created = await createEstimate({
      company_id: companyId,
      project_id: form.project_id || null,
      estimate_code: form.estimate_code.trim(),
      estimate_name: form.estimate_name.trim(),
      estimate_type: form.estimate_type,
      customer_name: form.customer_name.trim() || selectedProject?.client_name || selectedProject?.name || null,
      description: form.description.trim() || null,
      created_by: user?.id || null,
    });
    navigate(`/estimates/${created.id}`);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="flex-shrink-0 p-4">
        <PageHeaderNew
          title="Tạo dự toán cảnh quan"
          subtitle="Lập hồ sơ dự toán mới để bóc tách hạng mục, áp định mức và chuyển BOQ"
          onBack={() => navigate('/estimates')}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        <form onSubmit={handleSubmit} className="mx-auto max-w-3xl space-y-4">
          <Card className="border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Mã dự toán</span>
                <input
                  value={form.estimate_code}
                  onChange={(event) => setForm((current) => ({ ...current, estimate_code: event.target.value }))}
                  required
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
                />
              </label>

              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Loại hồ sơ</span>
                <select
                  value={form.estimate_type}
                  onChange={(event) => setForm((current) => ({ ...current, estimate_type: event.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
                >
                  <option value="project">Dự án</option>
                  <option value="quote">Báo giá lẻ</option>
                  <option value="work_order">Work Order</option>
                </select>
              </label>

              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-slate-600">Tên dự toán</span>
                <input
                  value={form.estimate_name}
                  onChange={(event) => setForm((current) => ({ ...current, estimate_name: event.target.value }))}
                  required
                  placeholder="Ví dụ: Dự toán cảnh quan khu công viên trung tâm"
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
                />
              </label>

              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Dự án liên kết</span>
                <select
                  value={form.project_id}
                  onChange={(event) => setForm((current) => ({ ...current, project_id: event.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
                >
                  <option value="">{isLoading ? 'Đang tải dự án...' : 'Không gắn dự án'}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Khách hàng</span>
                <input
                  value={form.customer_name}
                  onChange={(event) => setForm((current) => ({ ...current, customer_name: event.target.value }))}
                  placeholder={selectedProject?.client_name || 'Tên khách hàng'}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
                />
              </label>

              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-slate-600">Ghi chú phạm vi</span>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                />
              </label>
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => navigate('/estimates')} className="h-10 rounded-md border border-slate-200 bg-white px-4 text-sm text-slate-700 hover:bg-slate-50">
              Hủy
            </button>
            <button type="submit" disabled={isCreating || !form.estimate_name.trim()} className="h-10 rounded-md bg-emerald-700 px-4 text-sm text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60">
              {isCreating ? 'Đang tạo...' : 'Tạo dự toán'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
